import React, { useState, ReactElement, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PulseLoader from 'react-spinners/PulseLoader';
import he from 'he';
import Avatar from '../../atoms/Avatar';
import RenderPhoto from '../../atoms/RenderPhoto';
import StatusError from '../icons/StatusError';
import { tsSince } from '../../common';
import { getTextColor } from '../../style';
import { ternaryFunc } from '../../utils/helpers';
import {
  IBubble,
  IBubbleDeleted,
  IBubbleTail,
  IBubbleWrapper,
  IMessageBubbleProps,
  IStyledDeleteIcon,
} from './types/MessageBubble';
import { handleResendMessage, handleUnblurImage } from '../../actions/mailActions';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  marginBottom: 14,
  marginLeft: 10,
  zIndex: 1,
});

const BubbleWrapper = styled('div')<IBubbleWrapper>(
  {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    gap: 5,
  },
  ({ isSent }) => ({
    float: isSent ? 'right' : 'none',
    alignItems: isSent ? 'flex-end' : 'flex-start',
  })
);

const Bubble = styled('div')<IBubble>(
  {
    borderRadius: 4,
    marginLeft: 10,
    marginRight: 10,
    whiteSpace: 'pre-wrap',
    maxWidth: '45%',
    position: 'relative',
    overflowWrap: 'break-word',
  },
  ({ isSent, isError, isIndicator }) => ({
    boxShadow: isSent ? 'none' : '0px 1px 3px 0px rgba(0, 0, 0, 0.25)',
    top: isIndicator ? -1 : '',
    backgroundColor: isError ? '#cc0000' : ternaryFunc(isSent, '#1C9EC9', '#fff'),
    color: isSent ? '#ffffff' : ternaryFunc(!isError, '#000000', '#ffffff'),
    float: isSent ? 'right' : 'none',
    padding: '10px 14px',
  })
);

const BubbleDeleted = styled('div')<IBubbleDeleted>(
  {
    borderRadius: 4,
    margin: 10,
    whiteSpace: 'pre-wrap',
    maxWidth: '45%',
    position: 'relative',
    overflowWrap: 'break-word',
  },
  ({ isSent, isError }) => ({
    backgroundColor: isError ? '#cc0000' : 'none',
    color: !isError ? '#212121' : '#ffffff',
    float: isSent ? 'right' : 'none',
  })
);

const BubbleTime = styled('div')({
  marginLeft: 10,
  marginRight: 10,
  fontSize: 12,
});

const BubbleTail = styled('div')<IBubbleTail>(
  {
    position: 'absolute',
    borderStyle: 'solid',
  },
  ({ isSent, isError, isIndicator }) => ({
    bottom: isIndicator ? 12 : 30,
    right: isSent ? 5 : 'auto',
    left: isSent ? 'auto' : 5,
    borderWidth: isSent ? '8px 0px 8px 8px' : '0 8px 8px 0px',
    borderColor: isSent
      ? `transparent transparent transparent ${ternaryFunc(isError, 'rgb(204, 0, 0)', '#1C9EC9')}`
      : 'transparent #FFF transparent transparent',
    transform: isSent
      ? 'translateX(0%) translateY(10%) rotate(0deg)'
      : 'translateX(15%) translateY(10%) rotate(225deg)',
    boxShadow: isSent ? 'none' : '2px -2px 4px -2px rgba(0, 0, 0, 0.25)',
  })
);
const StyledDeleteIcon = styled('div')<IStyledDeleteIcon>(
  {
    position: 'absolute',
    top: -3,
    color: '#adadad',
  },
  ({ isSent }) => ({
    right: isSent ? 'unset' : -25,
    left: isSent ? -25 : 'unset',
  })
);

const MessageBubble = ({
  id,
  avatar,
  created_at,
  isRead,
  isSent,
  error,
  message,
  type,
  isBlurred = true,
  handleDeleteImg,
  handleReportImg,
  urls,
  isAdminMessage,
  threadUid,
  uid,
  ...rest
}: IMessageBubbleProps): ReactElement => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [date, setDate] = useState(created_at ? tsSince(created_at.date, false, false) : '');
  // messaging-sls returns is_visible: false to the receiver for a photo they hid via "Delete for Me"
  const isDeleted = rest.is_deleted || rest.is_visible === false;
  const showPhotoMessage = type === 'photo' && !isDeleted;
  const showDeleted = type === 'photo' && isDeleted;

  created_at && setInterval(() => setDate(tsSince(created_at.date, false, false)), 3000);

  const renderAvatar = useCallback(() => {
    return (
      !isSent && (
        <Avatar
          src={avatar}
          width={34}
          customStyle={{ border: '1px solid #DAE1E9', marginBottom: 10, height: 'auto' }}
        />
      )
    );
  }, [isSent, avatar]);

  const renderErrorStatus = useCallback((error: string | boolean | undefined, id: string | number | undefined) => {
    return (
      error && (
        <div
          onClick={() => dispatch(handleResendMessage(id, navigate, location, params))}
          style={{ cursor: 'pointer', position: 'absolute', left: -30, top: 0 }}
        >
          <StatusError />
        </div>
      )
    );
  }, []);

  const setEmojiSize = useCallback((textLength: number) => {
    switch (textLength) {
      case 4:
        return 42;
      case 6:
        return 32;
      default:
        return 60;
    }
  }, []);

  if (type === 'indicator') {
    return (
      <Container>
        <div>
          <BubbleWrapper isSent={isSent}>
            <Bubble isSent={isSent} isIndicator isError={error}>
              <PulseLoader color="#000000" size={8} />
            </Bubble>
            <BubbleTail isSent={isSent} isError={error} isIndicator={true} />
          </BubbleWrapper>
        </div>
      </Container>
    );
  }

  if (showPhotoMessage) {
    return (
      <Container>
        {renderAvatar()}
        <div style={{ width: '100%' }} id={id?.toString()}>
          <BubbleWrapper isSent={isSent}>
            <Bubble isError={error} isSent={isSent}>
              {renderErrorStatus(error, id)}
              <RenderPhoto
                error={error}
                messageId={id}
                handleDeleteImg={handleDeleteImg}
                handleUnblurImage={messageID => dispatch(handleUnblurImage(messageID))}
                handleReportImg={handleReportImg}
                hashId={rest.hash_id}
                isSent={isSent}
                isBlurred={isBlurred}
                photoUrl={message !== '' && JSON.parse(message)}
                urls={urls}
                threadUid={threadUid}
                uid={uid}
              />
            </Bubble>
            <BubbleTail isError={error} isSent={isSent} />
            <BubbleTime>
              {date}
              {ternaryFunc(isRead, ' • Read', '')}
            </BubbleTime>
          </BubbleWrapper>
        </div>
      </Container>
    );
  }

  if (type === 'emoji') {
    return (
      <Container>
        {renderAvatar()}
        <div style={{ width: '100%' }} id={id?.toString()}>
          <BubbleWrapper isSent={isSent}>
            <p
              data-test-id={`emoji-size-${setEmojiSize(message.length)}`}
              style={{ margin: '0 10px', fontSize: setEmojiSize(message.length) }}
            >
              {message}
            </p>
            <BubbleTime>
              {date}
              {ternaryFunc(isRead, ' • Read', '')}
            </BubbleTime>
          </BubbleWrapper>
        </div>
      </Container>
    );
  }

  if (showDeleted) {
    return (
      <Container>
        {renderAvatar()}
        <div style={{ width: '100%' }} id={id?.toString()}>
          <BubbleWrapper isSent={isSent}>
            <BubbleDeleted isError={error} isSent={isSent}>
              {renderErrorStatus(error, id)}
              {!error && (
                <StyledDeleteIcon isSent={isSent}>
                  <DeleteOutlineIcon />
                </StyledDeleteIcon>
              )}
              <p style={{ margin: error ? 10 : 0, ...getTextColor().DARK }}>This message is deleted</p>
            </BubbleDeleted>
            <BubbleTime>
              {date}
              {ternaryFunc(isRead, ' • Read', '')}
            </BubbleTime>
          </BubbleWrapper>
        </div>
      </Container>
    );
  }

  const escapedMessage = isAdminMessage ? message : he.encode(message);

  return (
    <Container>
      {renderAvatar()}
      <div style={{ width: '100%' }} id={id?.toString()}>
        <BubbleWrapper isSent={isSent}>
          <Bubble isError={error} isSent={isSent}>
            {renderErrorStatus(error, id)}
            <p style={{ margin: 0 }}>
              <div dangerouslySetInnerHTML={{ __html: escapedMessage }} />
            </p>
          </Bubble>
          <BubbleTail isError={error} isSent={isSent} />
          <BubbleTime>
            {date}
            {ternaryFunc(isRead, ' • Read', '')}
          </BubbleTime>
        </BubbleWrapper>
      </div>
    </Container>
  );
};

export default React.memo(MessageBubble);
